import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, ArrowDownToLine, ZoomIn, Sword, MousePointer, MousePointerClick, ChevronDown } from 'lucide-react';
import { HPIcon } from './icons/HPIcon';
import CardZoom from './CardZoom';
import { slugify } from '../utils/slugify';
import { EnergyIcon } from './icons/EnergyIcon';
import CollectionButton, { ALL_FINISH_OPTIONS, getFinishOptions } from './CollectionButton';
import CardImage from './CardImage';
import SentimentCheck from './SentimentCheck';
import ScrydexPricing from './ScrydexPricing';
import { useCardFinishes } from '../hooks/useCardFinishes';

interface CardDetailsProps {
  card: any;
}

export default function CardDetails({ card }: CardDetailsProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const availableFinishes = useCardFinishes(card?.id);

  if (!card) {
    return null;
  }

  // Get image URLs from the card data
  const imageUrl = card.image_large_url || 
    `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images-webp/pokemon_cards/${card.id}_large.webp`;

  return (
    <div className="p-3 sm:p-4 md:p-6 text-white">
      <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px] mx-auto">
        {/* Card Details Section - Now First */}
        <div className="flex-1 order-2 lg:order-none">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
            <h2 className="text-2xl font-bold">{card.name}</h2>
            {card.types?.[0] && <EnergyIcon type={card.types[0]} />}
            {card.hp && (
              <div className="flex items-center gap-1">
                <HPIcon className="text-red-500" size={20} />
                <span className="font-semibold">{card.hp} HP</span>
              </div>
            )}
          </div>

          {/* Card Information Section */}
          <div className="relative bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg mb-6 overflow-hidden">
            {/* Set Logo Background */}
            {card.set.images?.logo && (
              <div 
                className="absolute inset-0 opacity-5 pointer-events-none"
                style={{
                  backgroundImage: `url(${card.set.images.logo})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  transform: 'scale(0.65)'
                }}
              />
            )}
            <div className="relative z-10">
              <h3 className="text-lg font-semibold mb-4 text-white">Card Details</h3>
              {card.flavor && (
                <p className="italic text-white/80 mb-4">{card.flavor}</p>
              )}
              <div className="space-y-3">
                <p>
                  <span className="font-semibold text-gray-400">Set:</span>{' '}
                  <Link
                    to={`/${slugify(card.set.series)}/${slugify(card.set.name)}/`}
                    className="text-white hover:text-white/80 transition-colors underline"
                  >
                    {card.set.name}
                  </Link>
                </p>
                <p>
                  <span className="font-semibold text-gray-400">Series:</span>{' '}
                  <Link
                    to={`/${slugify(card.set.series)}/`}
                    className="text-white hover:text-white/80 transition-colors underline"
                  >
                    {card.set.series}
                  </Link>
                </p>
                <p><span className="font-semibold text-gray-400">Number:</span> {card.number}</p>
                <p><span className="font-semibold text-gray-400">Rarity:</span> {card.rarity}</p>
                <p>
                  <span className="font-semibold text-gray-400">Type:</span>{' '}
                  <span className="inline-flex items-center gap-2">
                    {card.types?.map((type: any, index: any) => (
                      <React.Fragment key={type}>
                        <Link
                          to={`/${slugify(card.set.series)}/${slugify(card.set.name)}/?type=${type}`}
                          className="inline-flex items-center gap-1 text-white hover:text-white/80 transition-colors"
                        >
                          <EnergyIcon type={type} />
                          {type}
                        </Link>
                        {index < card.types.length - 1 && ', '}
                      </React.Fragment>
                    ))}
                  </span>
                </p>
                <p>
                  <span className="font-semibold text-gray-400">Release Date:</span>{' '}
                  {new Date(card.set.releaseDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                {card.evolves_from && (
                  <p>
                    <span className="font-semibold text-gray-400">Evolves From:</span>{' '}
                    {card.evolves_from}
                  </p>
                )}
                {card.evolves_to && card.evolves_to.length > 0 && (
                  <p>
                    <span className="font-semibold text-gray-400">Evolves To:</span>{' '}
                    {card.evolves_to.join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Scrydex Pricing - Enhanced pricing with charts and trends */}
          <ScrydexPricing cardId={card.id} />

        </div>

        {/* Card Image Section - Now Last */}
        <div className="w-full lg:w-80 xl:w-96 mb-4 lg:mb-0 order-1 lg:order-last">
          <div className="relative max-w-[288px] mx-auto lg:mx-0">
            <CardImage
              src={imageUrl}
              alt={card.name}
              cardId={card.id}
              className="w-full rounded-lg shadow-md cursor-zoom-in"
              width={488}
              quality={90}
              onClick={() => setIsZoomed(true)}
            />

            {/* Zoom overlay */}
            <button
              onClick={() => setIsZoomed(true)}
              className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors rounded-lg">
              <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
            </button>
          </div>

          {/* Collection Management Section */}
          <div className="mt-4 bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">Add to Collection</h3>
            <div className="space-y-4">
              <div className="flex justify-start">
                <CollectionButton card={card} availableFinishes={availableFinishes} />
              </div>

              {(() => {
                const visibleFinishes = availableFinishes
                  ? getFinishOptions(availableFinishes)
                  : ALL_FINISH_OPTIONS;

                const finishList = (
                  <>
                    <p className="mb-2">Card Finishes:</p>
                    <ul className="list-none space-y-2">
                      {visibleFinishes.map(f => (
                        <li key={f.value} className="flex items-start gap-2">
                          <span className={`w-2 h-2 rounded-full ${f.dotColor} mt-[0.4375rem] flex-shrink-0`}></span>
                          <span><span className={f.textColor}>{f.label}</span> - {f.description}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                );

                return (
                  <>
                    {/* Desktop: Always visible */}
                    <div className="hidden lg:block text-sm text-white/60">
                      <div className="flex items-start gap-2 mb-3">
                        <MousePointer size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                        <span>Left click to add, right click to remove</span>
                      </div>
                      <div className="flex items-start gap-2 mb-4">
                        <MousePointerClick size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                        <span>You can add multiple copies of each finish</span>
                      </div>
                      {finishList}
                    </div>

                    {/* Mobile: Collapsible section */}
                    <div className="lg:hidden">
                      <button
                        onClick={() => setIsHowItWorksOpen(!isHowItWorksOpen)}
                        className="w-full flex items-center justify-between text-sm text-white/60 hover:text-white transition-colors py-2"
                      >
                        <span>How it works</span>
                        <ChevronDown
                          size={16}
                          className={`transform transition-transform ${isHowItWorksOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      
                      {isHowItWorksOpen && (
                        <div className="text-sm text-white/60 pt-2 space-y-4">
                          <div className="flex items-start gap-2 mb-3">
                            <MousePointer size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                            <span>Left click to add, right click to remove</span>
                          </div>
                          <div className="flex items-start gap-2 mb-4">
                            <MousePointerClick size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                            <span>You can add multiple copies of each finish</span>
                          </div>
                          {finishList}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Sentiment Check Section */}
          <SentimentCheck cardId={card.id} cardName={card.name} />
        </div>
      </div>
      
      <div className="mt-8 space-y-6">
        {/* Abilities Section */}
        {card.abilities && card.abilities.length > 0 && (
          <div className="mt-6 sm:mt-8 max-w-[1200px]">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Zap className="text-yellow-500" size={20} />
              Abilities
            </h3>
            <div className="grid gap-2">
              {card.abilities.map((ability: any, index: any) => (
                <div key={index} className="bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg">
                  <p className="font-semibold">{ability.name}</p>
                  <p className="text-sm text-gray-400 mt-1">{ability.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attacks Section */}
        {card.attacks && card.attacks.length > 0 && (
          <div className="mt-6 sm:mt-8 max-w-[1200px]">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Sword className="text-red-500" size={20} />
              Attacks
            </h3>
            <div className="grid gap-2">
              {card.attacks.map((attack: any, index: any) => (
                <div key={index} className="bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {attack.cost?.map((energyType: any, i: any) => (
                        <EnergyIcon key={i} type={energyType} />
                      ))}
                      <p className="font-semibold">{attack.name}</p>
                    </div>
                    <p className="font-bold">{attack.damage || '—'}</p>
                  </div>
                  {attack.text && (
                    <p className="text-sm text-gray-400 mt-1">{attack.text}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Weaknesses Section */}
        {card.weaknesses && card.weaknesses.length > 0 && (
          <div className="mt-6 sm:mt-8 max-w-[1200px]">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Shield className="text-purple-500" size={20} />
              Weaknesses
            </h3>
            <div className="flex flex-wrap gap-2">
              {card.weaknesses.map((weakness: any, index: any) => (
                <span key={index} className="bg-black/40 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full text-sm flex items-center gap-2">
                  <EnergyIcon type={weakness.type} />
                  {weakness.type} {weakness.value}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Retreat Cost Section */}
        {card.retreatCost && (
          <div className="mt-6 sm:mt-8 max-w-[1200px]">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <ArrowDownToLine className="text-gray-400" size={20} />
              Retreat Cost
            </h3>
            <div className="flex gap-2">
              {Array(card.retreatCost).fill(0).map((_, index) => (
                <EnergyIcon key={index} type="Colorless" />
              ))}
            </div>
          </div>
        )}
      </div>

      {isZoomed && (
        <CardZoom
          imageUrl={imageUrl}
          cardName={card.name}
          onClose={() => setIsZoomed(false)}
        />
      )}
    </div>
  );
}